const importAppShell = () => import('./app-shell');

let inFlight: ReturnType<typeof importAppShell> | null = null;

/**
 * The signed-in app's chunk (`app-shell.tsx`), one import for every caller:
 * `App.tsx` renders it lazily, and the landing fetches it ahead of a step that
 * is about to need it (a footer link to the blog, help, the legal pages), so
 * that step does not wait on ~3 MB with nothing on screen.
 *
 * Every caller shares ONE `import()` promise — not just for thrift. An app
 * path calls this twice before the chunk is in (App.tsx's eager fetch, then
 * `React.lazy` on first render), and so does a signed-in visit to `/` (the
 * landing's prefetch, then the redirect into the app). Safari / iOS WebKit up
 * to 16.3 resolves the SECOND concurrent `import()` of a module whose graph is
 * still loading with a namespace that has not been evaluated yet, so reading
 * the chunk's export threw "ReferenceError: Cannot access uninitialized
 * variable" and the app never mounted for those users (Sentry FLASHKA-VITE-2Q5,
 * -2Q1; same family as WebKit bug 242740). One promise means one import().
 *
 * A failed import is forgotten, so the next call (the real navigation, the
 * error page's reload) starts a fresh one instead of replaying the failure.
 */
export const loadAppShell = (): ReturnType<typeof importAppShell> => {
  if (!inFlight) {
    const attempt = importAppShell();
    inFlight = attempt;
    attempt.catch(() => {
      if (inFlight === attempt) inFlight = null;
    });
  }
  return inFlight;
};

/** Fetch the app shell ahead of need; a failed fetch is retried by the real
 *  navigation (and its error page), so it is swallowed here. */
export const prefetchAppShell = (): void => {
  void loadAppShell().catch(() => {});
};

/** The shell AND the pages a sign-in lands on (/onboarding, then /dashboard),
 *  for the landing's sign-in moment, so arrival waits on none of them. */
export const prefetchAppForSignIn = (): void => {
  void loadAppShell()
    .then((shell) => shell.prefetchArrivalPages())
    .catch(() => {});
};
